import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Loader2, Package, Clock, Shield, Truck, CheckCircle2, FileText, Settings, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useCart } from '../../context/CartContext';
import { largeFormatAPI } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import RecentlyViewed from '../../components/RecentlyViewed';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  if (imageUrl.startsWith('/')) {
    if (imageUrl.startsWith('/uploads/') && !imageUrl.startsWith('/api/')) {
      return `${API_URL}/api${imageUrl}`;
    }
    return `${API_URL}${imageUrl}`;
  }
  return imageUrl;
};

// Extract key features from description
const extractKeyFeatures = (description) => {
  if (!description) return [];
  
  // Clean up common concatenated labels from scraped content
  let cleanDesc = description
    .replace(/^DescriptionSpec(File Setup)?(Installation Guide)?/i, '')
    .replace(/DescriptionSpec(File Setup)?/gi, '')
    .trim();
  
  const features = [];
  const lines = cleanDesc.split(/[.\n]/).filter(l => l.trim().length > 10);
  
  for (const line of lines) {
    const trimmed = line.trim();
    // Skip if line looks like concatenated labels
    if (/^(description|spec|file setup|installation)/i.test(trimmed)) continue;
    if (trimmed.length > 15 && trimmed.length < 100 && features.length < 5) {
      features.push(trimmed);
    }
  }
  
  return features;
};

// Parse description into sections
const parseDescriptionSections = (description, apiSections = null) => {
  // If API already provides parsed sections, use them
  if (apiSections && (apiSections.main || apiSections.spec || apiSections.file_setup)) {
    return {
      main: apiSections.main || '',
      spec: apiSections.spec || '',
      fileSetup: apiSections.file_setup || ''
    };
  }
  
  if (!description) return { main: '', spec: '', fileSetup: '' };
  
  const sections = {
    main: '',
    spec: '',
    fileSetup: ''
  };
  
  // Clean up common merged tab labels from scraped content
  let cleanDesc = description
    .replace(/^DescriptionSpec(File Setup)?(Installation Guide)?/i, '')
    .replace(/DescriptionSpec(File Setup)?/gi, '\n---SECTION_BREAK---\n')
    .trim();
  
  const lowerDesc = cleanDesc.toLowerCase();
  
  // Try to find section markers
  const specMarkers = ['#### spec', '###spec', '## spec', 'specifications:', 'spec:'];
  const fileSetupMarkers = ['#### file setup', '###file setup', '## file setup', 'file setup:', 'file requirements:'];
  
  let specIndex = -1;
  let fileSetupIndex = -1;
  
  for (const marker of specMarkers) {
    const idx = lowerDesc.indexOf(marker);
    if (idx > -1 && (specIndex === -1 || idx < specIndex)) {
      specIndex = idx;
    }
  }
  
  for (const marker of fileSetupMarkers) {
    const idx = lowerDesc.indexOf(marker);
    if (idx > -1 && (fileSetupIndex === -1 || idx < fileSetupIndex)) {
      fileSetupIndex = idx;
    }
  }
  
  // Extract sections based on markers found
  if (specIndex > 0 && fileSetupIndex > specIndex) {
    sections.main = cleanDesc.substring(0, specIndex).trim();
    sections.spec = cleanDesc.substring(specIndex, fileSetupIndex).trim();
    sections.fileSetup = cleanDesc.substring(fileSetupIndex).trim();
  } else if (specIndex > 0) {
    sections.main = cleanDesc.substring(0, specIndex).trim();
    sections.spec = cleanDesc.substring(specIndex).trim();
  } else if (fileSetupIndex > 0) {
    sections.main = cleanDesc.substring(0, fileSetupIndex).trim();
    sections.fileSetup = cleanDesc.substring(fileSetupIndex).trim();
  } else {
    // No structure found - use full description as main
    // But try to split on common patterns
    const paragraphs = cleanDesc.split(/\n\n+/);
    if (paragraphs.length > 3) {
      // First 2 paragraphs as description
      sections.main = paragraphs.slice(0, 2).join('\n\n');
      // Rest could be specs
      sections.spec = paragraphs.slice(2).join('\n\n');
    } else {
      sections.main = cleanDesc;
    }
  }
  
  // Clean up section headers from content
  sections.main = sections.main.replace(/^(description|main|overview)[:\s]*/i, '').trim();
  sections.spec = sections.spec.replace(/^(spec|specifications|specs)[:\s]*/i, '').trim();
  sections.fileSetup = sections.fileSetup.replace(/^(file setup|file requirements|artwork)[:\s]*/i, '').trim();
  
  return sections;
};

const LargeFormatProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { recentlyViewed, addToRecentlyViewed } = useRecentlyViewed();
  
  // Product state
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  
  // For sq ft products
  const [width, setWidth] = useState('24');
  const [height, setHeight] = useState('36');
  const [quantity, setQuantity] = useState(1);
  
  // For fixed price products
  const [selectedSize, setSelectedSize] = useState('');
  
  // Common options
  const [selectedOptions, setSelectedOptions] = useState({});

  // Normalize product data from backend to expected frontend format
  const normalizeProduct = (data) => {
    const pricingType = data.pricing_type || data.priceType || 'fixed';
    const isPerSqFt = pricingType === 'per_sqft' || pricingType === 'sqft';
    
    let pricePerSqFt = 0;
    let basePrice = 0;
    
    if (isPerSqFt) {
      pricePerSqFt = data.sqft_pricing?.base_rate || data.retail_price || data.pricePerSqFt || 0;
    } else {
      basePrice = data.retail_price || data.basePrice || 0;
    }
    
    const imageUrl = data.image_url || data.image || (data.images && data.images[0]) || null;
    
    // Parse options - can be array of {name, values} or object
    let options = {};
    if (Array.isArray(data.options)) {
      data.options.forEach(opt => {
        if (opt.name && opt.values) {
          options[opt.name] = opt.values;
        }
      });
    } else if (data.options && typeof data.options === 'object') {
      options = data.options;
    }
    
    // Get size options - either from API or legacy sizeOptions field
    let sizeOptions = data.size_options || data.sizeOptions || [];
    
    // Normalize size options to have consistent fields
    if (sizeOptions.length > 0) {
      const baseRetailPrice = data.retail_price || 0;
      sizeOptions = sizeOptions.map((opt, idx) => ({
        name: opt.name || opt.size || `Size ${idx + 1}`,
        size: opt.size || opt.name || `Size ${idx + 1}`,  // Keep both for compatibility
        sku: opt.sku || `SIZE-${idx + 1}`,
        price: opt.price || baseRetailPrice  // Use provided price or base
      }));
    }
    
    // Parse description sections - prefer API-provided, fallback to parsing
    const apiSections = data.description_sections || null;
    const descSections = parseDescriptionSections(data.description || '', apiSections);
    
    return {
      ...data,
      id: data.id,
      name: data.name,
      description: data.description || '',
      descriptionSections: descSections,
      keyFeatures: extractKeyFeatures(data.description),
      sku: data.sku || data.internal_sku || '',
      priceType: isPerSqFt ? 'sqft' : 'fixed',
      pricePerSqFt: pricePerSqFt,
      basePrice: basePrice,
      minCharge: data.sqft_pricing?.min_charge || data.minCharge || 0,
      retailPrice: data.retail_price || 0,
      image: getImageUrl(imageUrl),
      gallery: (data.images || []).map(img => getImageUrl(img)),
      options: options,
      sizeOptions: sizeOptions,
      vendorLink: data.vendor_link || null,
      specifications: data.specifications || {
        material: data.source_category || null,
        features: data.tags || []
      }
    };
  };

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await largeFormatAPI.getProduct(productId);
        const data = response.product || response;
        const normalized = normalizeProduct(data);
        setProduct(normalized);
        
        if (normalized.priceType === 'fixed' && normalized.sizeOptions?.length > 0) {
          setSelectedSize(normalized.sizeOptions[0].sku);
        }
        
        const initialOptions = {};
        if (normalized.options && typeof normalized.options === 'object') {
          Object.keys(normalized.options).forEach(key => {
            const vals = normalized.options[key];
            if (Array.isArray(vals) && vals.length > 0) {
              initialOptions[key] = vals[0];
            }
          });
        }
        setSelectedOptions(initialOptions);
        
        addToRecentlyViewed({
          ...normalized,
          section: 'large-format'
        });
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [productId, addToRecentlyViewed]);

  // Calculate sq ft and price
  const { sqFt, calculatedPrice } = useMemo(() => {
    if (!product) return { sqFt: 0, calculatedPrice: 0 };
    
    if (product.priceType === 'sqft' && width && height) {
      const w = parseFloat(width) / 12;
      const h = parseFloat(height) / 12;
      const sqft = w * h;
      
      let price = sqft * product.pricePerSqFt;
      if (product.minCharge && price < product.minCharge) {
        price = product.minCharge;
      }
      
      price = price * quantity;
      
      if (selectedOptions.sides === '2 Sides') {
        price = price * 2;
      }
      
      return { sqFt: sqft, calculatedPrice: price };
    }
    
    if (product.priceType === 'fixed') {
      if (product.sizeOptions?.length > 0 && selectedSize) {
        const sizeOption = product.sizeOptions.find(s => s.sku === selectedSize);
        if (sizeOption) {
          let price = sizeOption.price;
          if (selectedOptions.type === 'Insert Only') {
            price = price * 0.5;
          }
          return { sqFt: 0, calculatedPrice: price * quantity };
        }
      }
      return { sqFt: 0, calculatedPrice: (product.retailPrice || product.basePrice) * quantity };
    }
    
    return { sqFt: 0, calculatedPrice: 0 };
  }, [product, width, height, quantity, selectedOptions.sides, selectedOptions.type, selectedSize]);

  const handleOptionChange = (optionType, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionType]: value
    }));
  };

  const handleAddToCart = () => {
    if (!product) return;

    const cartProduct = {
      ...product,
      basePrice: calculatedPrice / quantity,
      section: 'largeformat'
    };

    const options = product.priceType === 'sqft' 
      ? { ...selectedOptions, width: `${width}"`, height: `${height}"`, sqFt: sqFt.toFixed(2) }
      : { ...selectedOptions, size: product.sizeOptions?.find(s => s.sku === selectedSize)?.size || 'Standard' };

    addToCart(cartProduct, options, quantity);
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
      duration: 3000,
    });
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/cart');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-600 animate-spin mx-auto mb-4" />
          <p className="text-ivory-500 font-body">Loading product...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <Card className="bg-ivory-100 border border-ivory-300/50">
          <CardContent className="p-12 text-center">
            <p className="text-ivory-600 text-lg mb-4 font-body">Product not found</p>
            <Button className="btn-luxury-primary" asChild>
              <Link to="/LargePrint/products">Browse Products</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const guaranteeFeatures = [
    { icon: Package, text: 'Premium quality materials' },
    { icon: Clock, text: 'Fast turnaround time' },
    { icon: Shield, text: 'Satisfaction guaranteed' },
    { icon: Truck, text: 'Fast & reliable shipping' },
  ];

  return (
    <div className="min-h-screen bg-ivory-50">
      <div className="container mx-auto px-4 py-8">
        <Link 
          to="/LargePrint/products" 
          className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-6 transition-colors font-body"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
          {/* Left Column - Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <Card className="bg-ivory-100 border border-ivory-300/50 overflow-hidden">
              <div className="aspect-square bg-white">
                {product.image ? (
                  <img
                    src={product.gallery?.[selectedImage] || product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/600x600/f5f3f0/9a8b7d?text=Large+Format';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-24 h-24 text-ivory-400" />
                  </div>
                )}
              </div>
            </Card>
            
            {/* Gallery Thumbnails */}
            {product.gallery && product.gallery.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.gallery.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-colors ${
                      selectedImage === idx ? 'border-gold-500' : 'border-ivory-300/50 hover:border-gold-300'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Key Features List */}
            {product.keyFeatures && product.keyFeatures.length > 0 && (
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardContent className="p-4">
                  <ul className="space-y-2">
                    {product.keyFeatures.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-ivory-700 font-body">
                        <CheckCircle2 className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Product Info & Config */}
          <div className="space-y-6">
            {/* Product Title & SKU */}
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-2" data-testid="product-name">
                {product.name}
              </h1>
              {product.sku && (
                <Badge variant="outline" className="text-xs font-body">
                  SKU: {product.sku}
                </Badge>
              )}
            </div>

            {/* Price Display */}
            <div className="p-5 rounded-xl bg-gold-50 border border-gold-200">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-heading font-bold text-gold-600" data-testid="product-price">
                  ${product.pricePerSqFt > 0 ? product.pricePerSqFt.toFixed(2) : calculatedPrice.toFixed(2)}
                </span>
                {product.priceType === 'sqft' && (
                  <span className="text-lg text-ivory-600 font-body">per sq ft</span>
                )}
              </div>
            </div>

            {/* Configuration Card */}
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardContent className="p-6">
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-5">Configure Your Order</h3>
                
                <div className="space-y-5">
                  {/* Size Calculator for sqft products */}
                  {product.priceType === 'sqft' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="width" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Width (inches)</Label>
                          <Input
                            id="width"
                            type="number"
                            value={width}
                            onChange={(e) => setWidth(e.target.value)}
                            className="bg-white border-ivory-300 text-ivory-800"
                            min="1"
                            data-testid="width-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="height" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Height (inches)</Label>
                          <Input
                            id="height"
                            type="number"
                            value={height}
                            onChange={(e) => setHeight(e.target.value)}
                            className="bg-white border-ivory-300 text-ivory-800"
                            min="1"
                            data-testid="height-input"
                          />
                        </div>
                      </div>
                      
                      {/* Size Summary */}
                      <div className="bg-gold-500/10 p-4 rounded-lg">
                        <div className="flex justify-between items-center text-sm font-body">
                          <span className="text-ivory-600">{width}&quot; x {height}&quot; = {sqFt.toFixed(2)} sq ft</span>
                          <span className="text-gold-700 font-semibold">${calculatedPrice.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Size Selection for fixed price */}
                  {product.priceType === 'fixed' && product.sizeOptions && product.sizeOptions.length > 0 && (
                    <div>
                      <Label htmlFor="size" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Size</Label>
                      <Select value={selectedSize} onValueChange={setSelectedSize}>
                        <SelectTrigger id="size" className="bg-white border-ivory-300 text-ivory-800">
                          <SelectValue placeholder="Select a size" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-ivory-300">
                          {product.sizeOptions.map((sizeOpt) => (
                            <SelectItem key={sizeOpt.sku} value={sizeOpt.sku} className="text-ivory-800">
                              {sizeOpt.name || sizeOpt.size} - ${sizeOpt.price?.toFixed(2) || '0.00'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Dynamic Options - skip 'Size' if sizeOptions are shown */}
                  {product.options && Object.entries(product.options).map(([optionKey, optionValues]) => {
                    if (!Array.isArray(optionValues) || optionValues.length === 0) return null;
                    // Skip 'Size' option if we already have sizeOptions rendered
                    if (optionKey.toLowerCase() === 'size' && product.sizeOptions?.length > 0) return null;
                    const label = optionKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                      <div key={optionKey}>
                        <Label className="text-sm font-medium text-ivory-600 mb-2 block font-body">{label}</Label>
                        <Select 
                          value={selectedOptions[optionKey] || optionValues[0]} 
                          onValueChange={(value) => handleOptionChange(optionKey, value)}
                        >
                          <SelectTrigger className="bg-white border-ivory-300 text-ivory-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-ivory-300">
                            {optionValues.map((val) => (
                              <SelectItem key={val} value={val} className="text-ivory-800">{val}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}

                  {/* Quantity */}
                  <div>
                    <Label htmlFor="quantity" className="text-sm font-medium text-ivory-600 mb-2 block font-body">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="bg-white border-ivory-300 text-ivory-800 w-32"
                      min="1"
                      data-testid="quantity-input"
                    />
                  </div>
                </div>

                {/* Total */}
                <div className="mt-6 pt-4 border-t border-ivory-200">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-body text-ivory-600">Total</span>
                    <span className="text-2xl font-heading font-bold text-gold-600">${calculatedPrice.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full text-lg py-6 btn-luxury-primary rounded-xl font-body"
                onClick={handleBuyNow}
                data-testid="buy-now-btn"
              >
                Buy Now - ${calculatedPrice.toFixed(2)}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full text-lg py-6 btn-luxury-outline rounded-xl font-body"
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Guarantee Features */}
            <div className="grid grid-cols-2 gap-3">
              {guaranteeFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 p-3 rounded-lg bg-ivory-100 border border-ivory-300/50"
                >
                  <feature.icon className="w-4 h-4 text-gold-600 flex-shrink-0" />
                  <span className="text-xs text-ivory-600 font-body">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product Info Tabs */}
        <div className="max-w-7xl mx-auto mt-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start border-b border-ivory-200 bg-transparent h-auto p-0 mb-6">
              <TabsTrigger 
                value="description" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-gold-500 data-[state=active]:text-gold-700 rounded-none px-6 py-3 text-ivory-600 font-body font-medium"
                data-testid="tab-description"
              >
                <FileText className="w-4 h-4 mr-2" />
                Description
              </TabsTrigger>
              <TabsTrigger 
                value="spec" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-gold-500 data-[state=active]:text-gold-700 rounded-none px-6 py-3 text-ivory-600 font-body font-medium"
                data-testid="tab-spec"
              >
                <Settings className="w-4 h-4 mr-2" />
                Specifications
              </TabsTrigger>
              <TabsTrigger 
                value="filesetup" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-gold-500 data-[state=active]:text-gold-700 rounded-none px-6 py-3 text-ivory-600 font-body font-medium"
                data-testid="tab-filesetup"
              >
                <Info className="w-4 h-4 mr-2" />
                File Setup
              </TabsTrigger>
            </TabsList>

            {/* Description Tab */}
            <TabsContent value="description" className="mt-0">
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-ivory-800 mb-4">Description</h3>
                  <div className="prose prose-ivory max-w-none font-body text-ivory-600 leading-relaxed">
                    {product.descriptionSections?.main || product.description || 'No description available.'}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Specifications Tab */}
            <TabsContent value="spec" className="mt-0">
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-ivory-800 mb-4">Specifications</h3>
                  <div className="space-y-4 font-body text-ivory-600">
                    {product.descriptionSections?.spec ? (
                      <div className="prose prose-ivory max-w-none leading-relaxed">
                        {product.descriptionSections.spec}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {product.specifications?.material && (
                          <div className="flex">
                            <span className="font-semibold text-ivory-800 w-32">Material:</span>
                            <span>{product.specifications.material}</span>
                          </div>
                        )}
                        {product.priceType === 'sqft' && (
                          <div className="flex">
                            <span className="font-semibold text-ivory-800 w-32">Pricing:</span>
                            <span>Per square foot</span>
                          </div>
                        )}
                        <div className="flex">
                          <span className="font-semibold text-ivory-800 w-32">Print Type:</span>
                          <span>Full color UV printed - Indoor and outdoor ready</span>
                        </div>
                        <div className="flex">
                          <span className="font-semibold text-ivory-800 w-32">Finishing:</span>
                          <span>Free hemming and grommets included</span>
                        </div>
                        {product.specifications?.features && product.specifications.features.length > 0 && (
                          <div>
                            <span className="font-semibold text-ivory-800">Tags:</span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {product.specifications.features.map((feat, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{feat}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* File Setup Tab */}
            <TabsContent value="filesetup" className="mt-0">
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-ivory-800 mb-4">File Setup</h3>
                  <div className="space-y-4 font-body text-ivory-600">
                    {product.descriptionSections?.fileSetup ? (
                      <div className="prose prose-ivory max-w-none leading-relaxed">
                        {product.descriptionSections.fileSetup}
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <h4 className="font-semibold text-ivory-800">Accepted File Formats</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>PDF (single page only) - Preferred</li>
                            <li>JPEG, PNG, TIFF</li>
                            <li>AI, EPS, PSD (with fonts outlined)</li>
                          </ul>
                        </div>
                        
                        <div className="space-y-2">
                          <h4 className="font-semibold text-ivory-800">File Requirements</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Color Space: CMYK</li>
                            <li>Resolution: 150dpi minimum for large format</li>
                            <li>Max File Upload Size: 50MB</li>
                            <li>Submit artwork built to ordered size</li>
                            <li>Do not include crop marks or bleeds</li>
                          </ul>
                        </div>
                        
                        <div className="space-y-2">
                          <h4 className="font-semibold text-ivory-800">Additional Tips</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Convert Pantone/Spot colors to CMYK</li>
                            <li>Convert live fonts to outlines</li>
                            <li>Use provided design templates when available</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Recently Viewed Section */}
        <RecentlyViewed products={recentlyViewed} currentProductId={productId} />
      </div>
    </div>
  );
};

export default LargeFormatProductDetail;
